// ============================================================
// SEGUIMIENTO VSANA — flujo automático: imagen → IA → guarda
// No requiere botones ni campos manuales.
// ============================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/SeguimientoVsana.css';
import { API_URL } from '../config';

// ─── Tipos ───────────────────────────────────────────────────
type NombreUsuario = 'Gisbel' | 'Lourdes' | 'Ronald' | 'Liseth';

interface RegistroPasos {
    id: number;
    nombre_usuario: NombreUsuario;
    cantidad_pasos: number;
    meta_diaria: number;
    fecha_actividad: string;
    url_evidencia: string | null;
    fecha_subida: string;
}

type Estado = 'idle' | 'analizando' | 'ok' | 'ya_registrado' | 'error_ia' | 'error_nd';

// ─── Constantes ──────────────────────────────────────────────
const USUARIOS: NombreUsuario[] = ['Gisbel', 'Lourdes', 'Ronald', 'Liseth'];

const AVATAR_COLORES: Record<NombreUsuario, string> = {
    Gisbel: '#e879a0',
    Lourdes: '#a78bfa',
    Ronald: '#38bdf8',
    Liseth: '#34d399',
};

// ─── Componente ──────────────────────────────────────────────
const SeguimientoVsana: React.FC = () => {
    const [usuario, setUsuario] = useState<NombreUsuario>('Ronald');
    const [preview, setPreview] = useState<string | null>(null);
    const [isHeic, setIsHeic] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [filtroFecha, setFiltroFecha] = useState<string>(''); // formato YYYY-MM-DD
    const [estado, setEstado] = useState<Estado>('idle');
    const [resultado, setResultado] = useState<{ pasos: number; fecha: string | null } | null>(null);
    const [mensajeError, setMensajeError] = useState<string>('');
    const [registros, setRegistros] = useState<RegistroPasos[]>([]);
    const [cargando, setCargando] = useState(true);
    const inputFileRef = useRef<HTMLInputElement>(null);

    // ── Cargar historial ─────────────────────────────────────
    const cargarRegistros = useCallback(async () => {
        setCargando(true);
        try {
            const res = await fetch(`${API_URL}/api/vsana/pasos/`);
            if (res.ok) setRegistros(await res.json());
        } catch { /* silencioso */ }
        finally { setCargando(false); }
    }, []);

    useEffect(() => { cargarRegistros(); }, [cargarRegistros]);

    // ── Procesar imagen (analizar + guardar automáticamente) ──
    const procesarImagen = useCallback(async (file: File) => {
        // Mostrar preview
        setPreview(URL.createObjectURL(file));
        setEstado('analizando');
        setResultado(null);
        setMensajeError('');

        try {
            const fd = new FormData();
            fd.append('evidencia', file);
            fd.append('nombre_usuario', usuario);
            fd.append('meta_diaria', '10000');

            const res = await fetch(`${API_URL}/api/vsana/analizar/`, { method: 'POST', body: fd });
            const data = await res.json();

            if (!res.ok) {
                setEstado('error_ia');
                setMensajeError(data.error || 'Error al analizar la imagen.');
                return;
            }

            if (data.pasos === 0) {
                // IA no pudo leer los pasos
                setEstado('error_nd');
                return;
            }

            setResultado({ pasos: data.pasos, fecha: data.fecha });

            if (data.guardado) {
                if (data.ya_registrado) {
                    setEstado('ya_registrado');
                } else {
                    setEstado('ok');
                    cargarRegistros(); // refrescar historial
                }
            } else if (data.pasos > 0) {
                // IA detectó pasos pero no guardó (p.ej. fecha no detectada)
                setEstado('error_nd');
                setMensajeError('La IA no pudo leer la fecha. Inténtalo con otra imagen más clara.');
            }
        } catch {
            setEstado('error_ia');
            setMensajeError('Error de conexión. Verifica tu red.');
        }
    }, [usuario, cargarRegistros]);

    // ── Manejo de archivo ────────────────────────────────────
    const aplicarArchivo = (file: File) => {
        if (!file.type.startsWith('image/') && !file.name.toLowerCase().match(/\.(heic|heif)$/i)) return;
        procesarImagen(file);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) aplicarArchivo(f);
        // reset input so same file can be re-selected
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) aplicarArchivo(f);
    };

    // ── Reset (nueva foto) ───────────────────────────────────
    const resetear = () => {
        setPreview(null);
        setIsHeic(false);
        setEstado('idle');
        setResultado(null);
        setMensajeError('');
    };

    // ── Filtro de fecha ───────────────────────────────────────
    // Si no hay filtro explícito, buscar la fecha más reciente en los registros y usarla de filtro por defecto
    const fechaMasReciente = registros.length > 0
        ? registros.reduce((max, r) => r.fecha_actividad > max ? r.fecha_actividad : max, registros[0].fecha_actividad)
        : '';

    const fActivo = filtroFecha || fechaMasReciente;

    const registrosFiltrados = fActivo
        ? registros.filter(r => {
            // comparar solo DD-MM ignorando el año
            const [, mesR, diaR] = r.fecha_actividad.split('-'); // YYYY-MM-DD
            const [, mesF, diaF] = fActivo.split('-');
            return diaR === diaF && mesR === mesF;
        })
        : registros;

    // Obtener ranking ordenado (histórico global)
    const rankingTotal = registros.reduce((acc, curr) => {
        if (!acc[curr.nombre_usuario]) {
            acc[curr.nombre_usuario] = { ...curr, cantidad_pasos: 0, meta_diaria: 0, faltas: 0 };
        }
        acc[curr.nombre_usuario].cantidad_pasos += curr.cantidad_pasos;
        acc[curr.nombre_usuario].meta_diaria += curr.meta_diaria;
        if (curr.cantidad_pasos < 10000) acc[curr.nombre_usuario].faltas += 1;
        return acc;
    }, {} as Record<string, RegistroPasos & { faltas: number }>);

    const ranking = Object.values(rankingTotal).sort((a, b) => b.cantidad_pasos - a.cantidad_pasos);

    // ── Helpers ──────────────────────────────────────────────
    const porcentaje = (pasos: number, meta: number) =>
        Math.min(Math.round((pasos / meta) * 100), 100);

    return (
        <div className="vsana-page">
            {/* Header */}
            <header className="vsana-header">
                <div className="vsana-header-inner">
                    <span className="vsana-header-icon">🏃‍♂️</span>
                    <div>
                        <h1 className="vsana-header-title">Seguimiento VSana</h1>
                        <p className="vsana-header-sub">Sube tu foto — la IA registra los pasos automáticamente</p>
                    </div>
                </div>
            </header>

            <main className="vsana-main">
                {/* ─── Panel subida ──────────────────────────── */}
                <section className="vsana-card vsana-form-card">
                    <h2 className="vsana-section-title">📸 Registrar actividad</h2>

                    {/* Selector de usuario */}
                    <div className="vsana-field">
                        <label className="vsana-label">¿Quién eres?</label>
                        <div className="vsana-pills">
                            {USUARIOS.map(u => (
                                <button
                                    key={u}
                                    type="button"
                                    className={`vsana-pill ${usuario === u ? 'vsana-pill--active' : ''}`}
                                    style={{ ['--pill-color' as any]: AVATAR_COLORES[u] }}
                                    onClick={() => { setUsuario(u); resetear(); }}
                                >
                                    <span className="vsana-pill-dot" style={{ background: AVATAR_COLORES[u] }} />
                                    {u}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Dropzone */}
                    <div className="vsana-field">
                        <label className="vsana-label">Foto de evidencia</label>
                        <div
                            className={`vsana-dropzone ${dragging ? 'vsana-dropzone--drag' : ''} ${preview ? 'vsana-dropzone--has-preview' : ''}`}
                            onDragOver={e => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                            onClick={() => estado !== 'analizando' && inputFileRef.current?.click()}
                        >
                            {preview ? (
                                isHeic ? (
                                    <div className="vsana-heic-preview">
                                        <span className="vsana-heic-icon">📱</span>
                                        <p>Imagen HEIC cargada</p>
                                        <small>(Vista previa no disponible, pero se analizará correctamente)</small>
                                    </div>
                                ) : (
                                    <img src={preview} alt="Preview" className="vsana-preview-img" />
                                )
                            ) : (
                                <div className="vsana-dropzone-inner">
                                    <span className="vsana-dropzone-icon">📸</span>
                                    <span className="vsana-dropzone-text">
                                        Arrastra tu foto aquí o <strong>haz clic para elegir</strong>
                                    </span>
                                    <span className="vsana-dropzone-hint">JPG, PNG o HEIC · máx. 10 MB</span>
                                </div>
                            )}
                            <input
                                ref={inputFileRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileInput}
                            />
                        </div>

                        {/* Estados del proceso */}
                        {estado === 'analizando' && (
                            <div className="vsana-status vsana-status--analyzing">
                                <span className="vsana-spinner" />
                                Analizando imagen con IA…
                            </div>
                        )}
                        {estado === 'ok' && resultado && (
                            <div className="vsana-status vsana-status--ok">
                                ✅ ¡Registrado! — {resultado.pasos.toLocaleString('es-CL')} pasos
                                {resultado.fecha ? ` · ${resultado.fecha}` : ''}
                                <button className="vsana-btn-reset" onClick={resetear}>Subir otra foto</button>
                            </div>
                        )}
                        {estado === 'ya_registrado' && resultado && (
                            <div className="vsana-status vsana-status--warn">
                                ⚠️ Ya registraste hoy — {resultado.pasos.toLocaleString('es-CL')} pasos detectados (registro actualizado)
                                <button className="vsana-btn-reset" onClick={resetear}>Subir otra foto</button>
                            </div>
                        )}
                        {estado === 'error_nd' && (
                            <div className="vsana-status vsana-status--warn">
                                ⚠️ {mensajeError || 'La IA no pudo leer los pasos. Intenta con una foto más clara.'}
                                <button className="vsana-btn-reset" onClick={resetear}>Intentar de nuevo</button>
                            </div>
                        )}
                        {estado === 'error_ia' && (
                            <div className="vsana-status vsana-status--error">
                                ❌ {mensajeError}
                                <button className="vsana-btn-reset" onClick={resetear}>Intentar de nuevo</button>
                            </div>
                        )}
                    </div>
                </section>

                {/* ─── Podio / Ranking ───────────────────────── */}
                {ranking.length > 0 && (
                    <section className="vsana-card vsana-ranking-card">
                        <h2 className="vsana-section-title">🏆 Ranking Global</h2>
                        <div className="vsana-ranking-list">
                            {ranking.map((r, i) => {
                                const color = AVATAR_COLORES[r.nombre_usuario as NombreUsuario] ?? '#ccc';
                                const pct = porcentaje(r.cantidad_pasos, r.meta_diaria);
                                return (
                                    <div key={r.id} className="vsana-ranking-item">
                                        <div className="vsana-ranking-pos">
                                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}º`}
                                        </div>
                                        <div className="vsana-ranking-avatar" style={{ backgroundColor: color }}>
                                            {r.nombre_usuario.charAt(0)}
                                        </div>
                                        <div className="vsana-ranking-info">
                                            <div className="vsana-ranking-name">{r.nombre_usuario}</div>
                                            <div className="vsana-ranking-steps">{r.cantidad_pasos.toLocaleString('es-CL')} pasos</div>
                                            {r.faltas > 0 && (
                                                <div className="vsana-ranking-faltas">❌ {r.faltas} {r.faltas === 1 ? 'falta' : 'faltas'}</div>
                                            )}
                                        </div>
                                        <div className="vsana-ranking-progress">
                                            <div className="vsana-ranking-bar">
                                                <div className="vsana-ranking-fill" style={{ width: `${pct}%`, backgroundColor: color }} />
                                            </div>
                                            <span className="vsana-ranking-pct">{pct}%</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* ─── Feed ─────────────────────────────────── */}
                <section className="vsana-card vsana-feed-card">
                    <h2 className="vsana-section-title">📊 Historial del grupo</h2>

                    {/* Filtro por día y mes */}
                    <div className="vsana-filter-row">
                        <label className="vsana-label">Filtrar por fecha:</label>
                        <input
                            type="date"
                            className="vsana-input vsana-filter-date"
                            value={filtroFecha}
                            onChange={e => setFiltroFecha(e.target.value)}
                        />
                        {filtroFecha && (
                            <button className="vsana-btn-reset" style={{ color: '#6b7280', border: '1px solid #d1d5db' }} onClick={() => setFiltroFecha('')}>
                                ✕ Limpiar
                            </button>
                        )}
                    </div>

                    {cargando ? (
                        <div className="vsana-loading">Cargando registros…</div>
                    ) : registrosFiltrados.length === 0 ? (
                        <div className="vsana-empty">{filtroFecha ? 'Sin registros para esa fecha.' : 'Todavía no hay registros. ¡Sé el primero!'}</div>
                    ) : (
                        <div className="vsana-feed">
                            {registrosFiltrados.map(r => {
                                const pct = porcentaje(r.cantidad_pasos, r.meta_diaria);
                                const color = AVATAR_COLORES[r.nombre_usuario as NombreUsuario] ?? '#6b7280';
                                return (
                                    <article key={r.id} className="vsana-entry">
                                        <div className="vsana-entry-left">
                                            <div className="vsana-avatar" style={{ background: color }}>
                                                {r.nombre_usuario[0]}
                                            </div>
                                            <div className="vsana-entry-info">
                                                <span className="vsana-entry-name">{r.nombre_usuario}</span>
                                                <span className="vsana-entry-date">{r.fecha_actividad}</span>
                                            </div>
                                        </div>

                                        <div className="vsana-entry-center">
                                            <div className="vsana-entry-steps">
                                                <span className="vsana-steps-count">
                                                    {r.cantidad_pasos.toLocaleString('es-CL')}
                                                </span>
                                                <span className="vsana-steps-meta">
                                                    / {r.meta_diaria.toLocaleString('es-CL')} pasos
                                                </span>
                                            </div>
                                            <div className="vsana-bar-bg">
                                                <div className="vsana-bar-fill" style={{ width: `${pct}%`, background: color }} />
                                            </div>
                                            <span className="vsana-pct">{pct}% de la meta {pct >= 100 ? '🎉' : ''}</span>
                                        </div>

                                        {r.url_evidencia && (
                                            <a href={r.url_evidencia} target="_blank" rel="noopener noreferrer" className="vsana-entry-thumb">
                                                <img src={r.url_evidencia} alt="Evidencia" />
                                                <span className="vsana-thumb-overlay">Ver</span>
                                            </a>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default SeguimientoVsana;
